import React, { createContext, useContext, useEffect, useState, useCallback } from "react"
import i18n from "../i18n/config"
import { DEFAULT_LOCALE, DEFAULT_REGION, IN_PLAY_KEY, LANGUAGES, STORAGE_KEY } from "../i18n/languages"
import {
	isLangParamEnabled,
	readLangParam,
	regionToUrlLang,
	resolveRegion,
	urlLangToRegion,
	withLangParam,
	withLangPath,
} from "../i18n/urlLang"
import { sendGAEvent } from "@utils/analytics"
import { publishAnalyticsLocale } from "@utils/analytics/context"

const REGION_CODES = LANGUAGES.map((l) => l.code)

const readUrlRegion = () => {
	if (typeof window === "undefined" || !isLangParamEnabled()) return null
	return urlLangToRegion(readLangParam(window.location.search))
}

const readStoredRegion = () => {
	if (typeof window === "undefined") return DEFAULT_REGION
	try {
		const region = resolveRegion(window.localStorage.getItem(STORAGE_KEY))
		if (region) return region
	} catch {
		// localStorage unavailable (private mode, etc.) — fall back silently
	}
	return DEFAULT_REGION
}

const readInitialRegion = () => readUrlRegion() ?? readStoredRegion()

const readInPlayFlag = () => {
	try {
		return window.localStorage.getItem(IN_PLAY_KEY) === "1"
	} catch {
		return false
	}
}

const writeStorage = (key, value) => {
	try {
		window.localStorage.setItem(key, value)
		return true
	} catch {
		return false
	}
}

const syncUrlLang = (region) => {
	const { pathname, search, hash } = window.location
	if (readLangParam(search) === regionToUrlLang(region)) return
	window.history.replaceState(window.history.state, "", `${pathname}${withLangParam(search, region)}${hash}`)
}

export const LocaleContext = createContext({
	locale: DEFAULT_REGION,
	i18nLocale: DEFAULT_LOCALE,
	urlLang: regionToUrlLang(DEFAULT_REGION),
	langInUrl: false,
	setLocale: () => {},
	withLang: (to) => to,
	languages: LANGUAGES,
})

export const LocaleProvider = ({ children }) => {
	const [locale, setLocaleState] = useState(readInitialRegion)
	const [langInUrl, setLangInUrl] = useState(false)

	const entry = LANGUAGES.find((l) => l.code === locale) || LANGUAGES[0]

	useEffect(() => {
		if (!isLangParamEnabled()) return
		if (readUrlRegion() || readInPlayFlag()) setLangInUrl(true)
	}, [])

	useEffect(() => {
		publishAnalyticsLocale({ region: locale })
		if (i18n.language !== entry.locale) {
			i18n.changeLanguage(entry.locale)
		}
		if (typeof window !== "undefined") {
			try {
				window.localStorage.setItem(STORAGE_KEY, locale)
			} catch {
				// ignore write failures
			}
		}
	}, [locale, entry.locale])

	useEffect(() => {
		if (!langInUrl || typeof window === "undefined") return
		writeStorage(IN_PLAY_KEY, "1")
		syncUrlLang(locale)
	}, [locale, langInUrl])

	const setLocale = useCallback((next, source = "selector") => {
		if (!REGION_CODES.includes(next)) return
		if (next !== locale) {
			publishAnalyticsLocale({ region: next, source })
			sendGAEvent("language_change", {
				previous_language: regionToUrlLang(locale),
				site_language: regionToUrlLang(next),
				language_source: source,
			})
		}
		setLocaleState(next)
		if (isLangParamEnabled()) setLangInUrl(true)
	}, [locale])

	const withLang = useCallback(
		(to) => (langInUrl ? withLangPath(to, locale) : to),
		[langInUrl, locale]
	)

	return (
		<LocaleContext.Provider
			value={{
				locale,
				i18nLocale: entry.locale,
				urlLang: regionToUrlLang(locale),
				langInUrl,
				setLocale,
				withLang,
				languages: LANGUAGES,
			}}
		>
			{children}
		</LocaleContext.Provider>
	)
}

export const useLocale = () => useContext(LocaleContext)
